import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Terminal, FileSpreadsheet } from 'lucide-react';

export type DataProviderId = 'alpaca' | 'polygon' | 'bloomberg' | 'csv';

interface DataProvider {
  id: DataProviderId;
  name: string;
  description: string;
  icon: React.ReactNode;
}

interface DataProviderSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProviderSelect: (providerId: DataProviderId) => void;
}

const dataProviders: DataProvider[] = [
  {
    id: 'alpaca',
    name: 'Alpaca',
    description: 'Connect to Alpaca for commission-free trading with live market data',
    icon: (
      <div className="bg-yellow-300 rounded-full h-10 w-10 flex items-center justify-center">
        <img 
          src="/alpaca-logo.svg" 
          alt="Alpaca Logo" 
          className="w-8 h-8 object-contain"
        />
      </div>
    )
  },
  {
    id: 'polygon',
    name: 'Polygon.io',
    description: 'Access real-time and historical market data via Polygon.io',
    icon: (
      <div className="bg-blue-700 rounded-full h-10 w-10 flex items-center justify-center">
        <img 
          src="/polygon-logo.svg" 
          alt="Polygon.io Logo" 
          className="w-8 h-8 object-contain"
        />
      </div>
    )
  },
  {
    id: 'bloomberg',
    name: 'Bloomberg Terminal',
    description: 'Connect via Stewie Bridge to use your Bloomberg Terminal data',
    icon: (
      <div className="bg-orange-100 rounded-full h-10 w-10 flex items-center justify-center">
        <Terminal className="h-6 w-6 text-orange-500" />
      </div>
    )
  },
  {
    id: 'csv',
    name: 'Custom Data Integration',
    description: 'Connect your proprietary data sources and custom integrations',
    icon: (
      <div className="bg-purple-100 rounded-full h-10 w-10 flex items-center justify-center">
        <FileSpreadsheet className="h-6 w-6 text-purple-500" />
      </div>
    )
  }
];

export const DataProviderSelectionDialog: React.FC<DataProviderSelectionDialogProps> = ({
  open,
  onOpenChange,
  onProviderSelect
}) => {
  const handleProviderClick = (providerId: DataProviderId) => {
    if (providerId === 'polygon') {
      // Open Polygon.io website in a new tab
      window.open('https://polygon.io/', '_blank');
    } else if (providerId === 'alpaca') {
      // Open Alpaca website in a new tab
      window.open('https://alpaca.markets/', '_blank');
    } else {
      // Handle normal selection for other providers
      onProviderSelect(providerId);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Select Data Provider</DialogTitle>
          <DialogDescription>
            Choose a source to import your portfolio and trading data
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 gap-4 pt-4">
          {dataProviders.map((provider) => (
            <Card 
              key={provider.id}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-4"
              onClick={() => handleProviderClick(provider.id)}
            >
              <div className="flex-shrink-0 ml-2">
                {provider.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                <p className="text-sm text-gray-600">{provider.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};